import capitalize from 'lodash/capitalize';

import {
  Box,
  Column,
  DataTable,
  TableHeader,
  TableRecordsCount,
  Tooltip,
  Typography,
  tokens,
} from '@cast/design-system';

import { CatalogueItem, toNotificationSeverity } from './constants';
import { useDefinitionDrawer } from './useDefinitionDrawer';
import { SeverityChip } from '../_components';

type Props = {
  items: CatalogueItem[];
  isLoading?: boolean;
};

/**
 * Severity cell: use the canonical StatusChipV2-based chip when the API
 * severity maps to the legacy enum; raw capitalized text as the fallback
 * for severities outside the enum (the contract is a plain string).
 */
const SeverityCell = ({ severity }: { severity: string }) => {
  const mapped = toNotificationSeverity(severity);

  if (!mapped) {
    return <Typography variant="P12R">{capitalize(severity)}</Typography>;
  }
  return <SeverityChip severity={mapped} size="small" />;
};

export const CatalogueTable = ({ items, isLoading }: Props) => {
  const { openDefinitionDrawer } = useDefinitionDrawer();

  const header = (
    <TableHeader
      recordsCount={
        <TableRecordsCount count={items.length} title="notification" />
      }
    />
  );

  return (
    <DataTable<CatalogueItem>
      data={items}
      isLoading={isLoading}
      testId="notifications-catalogue-table"
      outerHeader={header}
      onRowClick={(_, { row }) => openDefinitionDrawer({ item: row })}
      sx={{
        width: '100%',
        '& .DS-Table-BodyRow': {
          cursor: 'pointer',
        },
      }}
    >
      <Column<CatalogueItem>
        id="name"
        header="Notification definition"
        minWidth={200}
        sortable
        renderCell={(item) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              minWidth: 0,
            }}
          >
            <Typography variant="P12M" noWrap>
              {item.name}
            </Typography>
          </Box>
        )}
      />
      <Column<CatalogueItem>
        id="description"
        header="Description"
        minWidth={420}
        // Compact one-liner: truncated with ellipsis, full text on hover;
        // the drawer always shows everything.
        renderCell={({ description }) => (
          <Tooltip title={description} placement="top" arrow>
            <Typography
              variant="P12R"
              color={tokens.theme.fg.structural.secondary}
              noWrap
            >
              {description}
            </Typography>
          </Tooltip>
        )}
      />
      <Column<CatalogueItem>
        id="domain"
        header="Domain"
        minWidth={160}
        sortable
        renderCell={({ domain }) => (
          <Typography variant="P12R">{capitalize(domain)}</Typography>
        )}
      />
      <Column<CatalogueItem>
        id="severity"
        header="Severity"
        minWidth={120}
        sortable
        renderCell={({ severity }) => <SeverityCell severity={severity} />}
      />
    </DataTable>
  );
};
